/**
 * 44 × 44 px wherever a finger is the pointer (F-0229, AC-4.2) — every control the decisions
 * surfaces draw carries one of these, and so do other surfaces' small controls (the All work areas
 * item panel's header, I-0723). Here in `components` rather than under decisions for that reason: a
 * module outside decisions that imported it from there read, to the decisions-split guard, as a
 * module laying a decision out.
 *
 * Two conditions, because neither alone finds every finger:
 *
 * - **Below `sm`** — a phone, in the browser or the app. The rule the signed-out pages already use
 *   (I-0653), and the one a desktop browser's narrow window also meets, which is harmless.
 * - **A coarse primary pointer** — the iPad, which lays the desktop out at 768–1366px and is still
 *   touched. Its compact controls measured 24–28px there. A width rule cannot see a finger; this can.
 *
 * `min-` sizes, not `h-`/`w-`, so that the target grows around a control's own size rather than
 * fighting it: `Button`'s `sm:h-7` and a row's padding stay as they are wherever the pointer is fine,
 * and a `min-height` wins over a `height` whatever order the classes are generated in.
 *
 * `touch` is 44 px (`tailwind.config.js`), not `11` (2.75rem): in `rem` every control carrying this
 * had a 137 px floor at the largest text size (I-1294). A label still grows the control; the floor
 * does not grow with it.
 */
export const TOUCH_TARGET = 'max-sm:min-h-touch [@media(pointer:coarse)]:min-h-touch';

/** The same, in both directions — for a control whose own width can fall under 44px. */
export const TOUCH_TARGET_SQUARE = `${TOUCH_TARGET} max-sm:min-w-touch [@media(pointer:coarse)]:min-w-touch`;

/**
 * An icon alone in its box: 44 × 44 wherever a finger is the pointer, at every text size, with the
 * glyph growing with the text up to what the box holds (I-1294). A height and a width, not floors:
 * the control's own `rem` size (`h-7 w-7`) is 87 px at the largest text size, over the floor. For a
 * control with a label, use `TOUCH_TARGET` or `TOUCH_TARGET_SQUARE`, which let it grow.
 */
export const TOUCH_BOX =
  'max-sm:h-touch max-sm:w-touch [@media(pointer:coarse)]:h-touch [@media(pointer:coarse)]:w-touch [&>svg]:touch-glyph';

/**
 * 16px text in a field wherever a finger is the pointer (I-0719). iOS Safari, and a `WKWebView` with
 * default settings, zoom the whole page when a field under 16px takes focus, so answering in your
 * own words zoomed the screen. The same two conditions as `TOUCH_TARGET`; wider and fine pointers
 * keep the field's own size.
 */
export const FIELD_TEXT_NO_ZOOM = 'max-sm:text-base [@media(pointer:coarse)]:text-base';

/**
 * A labelled button in a row that may be narrower than its label at the largest text size (T-0318,
 * I-1331's row of add actions): the label wraps inside the button instead of running off the edge.
 */
export const ROW_ACTION_WRAPS = 'h-auto max-w-full whitespace-normal text-left';

/**
 * F-0590 (IP-34, `docs/concept-mobile-app.md`): a control in a row of a page's header or an item's
 * actions, drawn at the desktop's height under a coarse pointer from the desktop layout up, and
 * pressed over an invisible 44 × 44 extension centred on it (`.coarse-hit`, `index.css`). The coarse
 * floor (`[data-coarse-targets]`, `[data-touch-row]`) gives way to it; under 768 px the phone's own
 * floors stay.
 */
export const COARSE_HIT = 'coarse-hit';

/**
 * The same, for a control that keeps a floor of its own with a mouse (`min-h-8`): it is drawn at
 * that floor, not under it.
 */
export const COARSE_HIT_FLOOR_32 = 'coarse-hit [--coarse-hit-min-height:theme(spacing.8)]';

/**
 * The same, for an icon control or a short label in a row: drawn at the desktop's height and at
 * least a finger's width, so its extension reaches past it only above and below, never onto a
 * neighbour across the row's 8 px (the phone's compact `⋯` is drawn so too).
 */
export const COARSE_HIT_WIDE = 'coarse-hit [--coarse-hit-min-width:theme(spacing.touch)]';
